import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import useRouteMeta from '../../../hooks/useRouteMeta';
import './ContactUsPage.css';
import { websiteAPI } from '../../../services/merchantAPI';
import { masterDataConfigAPI } from '../../../services/api';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { Popover, List, ListItemButton, ListItemText, TextField, InputAdornment, Box, Typography } from '@mui/material';
import { Search as SearchIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

// Google reCAPTCHA v2 checkbox — the script is loaded once and the widget rendered explicitly
const RECAPTCHA_SCRIPT_ID = 'google-recaptcha-script';
const RECAPTCHA_WIDTH = 304;
const RECAPTCHA_HEIGHT = 78;

const loadRecaptchaScript = () => new Promise((resolve, reject) => {
  if (window.grecaptcha && window.grecaptcha.render) {
    resolve(window.grecaptcha);
    return;
  }
  window.onGlammifyRecaptchaLoad = () => resolve(window.grecaptcha);
  if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onGlammifyRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = reject;
    document.body.appendChild(script);
  }
});

const ContactUsPage = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    mobile_code: '+91',
    message: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    mobile: '',
    mobile_code: '',
    message: '',
    recaptcha: ''
  });

  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaContainerRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);
  const recaptchaWrapperRef = useRef(null);
  const [recaptchaScale, setRecaptchaScale] = useState(1);

  const [countries, setCountries] = useState([]);
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Load countries and detect default country from IP
  useEffect(() => {
    const loadCountriesAndDetect = async () => {
      try {
        const countriesResponse = await masterDataConfigAPI.getCountries();
        const countryList = countriesResponse.data.data || [];
        setCountries(countryList);

        // Detect country from IP
        try {
          const detectResponse = await merchantAuthAPI.detectCountry();
          if (detectResponse.data && detectResponse.data.country_code) {
            setFormData(prev => ({
              ...prev,
              mobile_code: detectResponse.data.country_code
            }));
          }
        } catch (detectErr) {
          console.error('Failed to detect country from IP:', detectErr);
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
      }
    };
    loadCountriesAndDetect();
  }, []);

  // reCAPTCHA is shown only when the backend has keys configured
  useEffect(() => {
    websiteAPI.getRecaptchaConfig()
      .then((response) => {
        if (response.data && response.data.enabled && response.data.site_key) {
          setRecaptchaSiteKey(response.data.site_key);
        }
      })
      .catch((error) => console.error('Failed to load reCAPTCHA config:', error));
  }, []);

  useEffect(() => {
    if (!recaptchaSiteKey) return;
    let cancelled = false;

    // Renders into a fresh element each time — grecaptcha.render() can't reuse a node,
    // and reset() leaves Google's own "Verification expired" text inside the widget.
    const renderWidget = (grecaptcha) => {
      const host = recaptchaContainerRef.current;
      if (cancelled || !host) return;
      host.innerHTML = '';
      const el = document.createElement('div');
      host.appendChild(el);
      recaptchaWidgetIdRef.current = grecaptcha.render(el, {
        sitekey: recaptchaSiteKey,
        callback: (token) => {
          setRecaptchaToken(token);
          setErrors(prev => ({ ...prev, recaptcha: '' }));
        },
        // A tick is valid for ~2 minutes: swap in a clean checkbox and show our own message below it
        'expired-callback': () => {
          setRecaptchaToken('');
          renderWidget(grecaptcha);
          setErrors(prev => ({ ...prev, recaptcha: 'Verification expired. Please tick the box again.' }));
        },
        'error-callback': () => setRecaptchaToken(''),
      });
    };

    loadRecaptchaScript()
      .then((grecaptcha) => {
        if (recaptchaWidgetIdRef.current === null) renderWidget(grecaptcha);
      })
      .catch((error) => console.error('Failed to load reCAPTCHA:', error));
    return () => { cancelled = true; };
  }, [recaptchaSiteKey]);

  // Google's widget is a fixed 304px iframe — scale it down so it never overflows the form on small phones
  useEffect(() => {
    const wrapper = recaptchaWrapperRef.current;
    if (!recaptchaSiteKey || !wrapper) return;
    const updateScale = () => setRecaptchaScale(Math.min(1, wrapper.clientWidth / RECAPTCHA_WIDTH));
    updateScale();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScale);
      return () => window.removeEventListener('resize', updateScale);
    }
    const observer = new ResizeObserver(updateScale);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [recaptchaSiteKey]);

  // Tokens are single-use: reset the checkbox after every submit attempt
  const resetRecaptcha = () => {
    setRecaptchaToken('');
    if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
  };

  const validateEmail = (email) => {
    // RFC 5322 compliant email regex that properly handles all valid email characters
    // This regex allows all valid special characters including +, -, ., etc.
    // It follows the official email specification more closely
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Clear submit message when user starts typing
    if (submitMessage) {
      setSubmitMessage('');
    }
  };

  const handleCountryClick = (event) => {
    setCountryAnchorEl(event.currentTarget);
    setCountrySearch('');
  };

  const handleCountryClose = () => {
    setCountryAnchorEl(null);
    setCountrySearch('');
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      mobile_code: country.country_code
    }));
    handleCountryClose();
  };

  const filteredCountries = countries.filter(c =>
    (c.country_name || '').toLowerCase().includes(countrySearch.toLowerCase()) ||
    (c.country_code || '').includes(countrySearch)
  );

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{5,15}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Please enter a valid mobile number (5-15 digits)';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    if (recaptchaSiteKey && !recaptchaToken) {
      newErrors.recaptcha = "Please confirm you're not a robot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await websiteAPI.contactUs({ ...formData, recaptcha_token: recaptchaToken });
      
      if (response.data.success) {
        setSubmitMessage(response.data.message);
        // Clear form on successful submission
        setFormData({
          name: '',
          email: '',
          mobile: '',
          mobile_code: '+91',
          message: ''
        });
        // Scroll to top after successful submission
        setTimeout(() => {
          scrollToTop();
        }, 100);
      } else {
        setSubmitMessage(response.data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Contact us submission error:', error);
      
      if (error.response && error.response.data && error.response.data.error) {
        setSubmitMessage(error.response.data.error);
      } else {
        setSubmitMessage('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
      resetRecaptcha();
    }
  };

  return (
    <div className="contact-us-page">
      <Header />
      <div className="comman-gradient-header comman-gradient-header-minimal bg-linear-gradient-30">
      </div>
        <div className="container contact-us-container">
          <div className="row justify-content-center">
            <div className="col-lg-12 col-xxl-10">
              <div className="contact-card">
                <div className="row">
                  {/* Left Section - Contact Form */}
                  <div className="col-lg-6">
                    <div className="contact-form-section">
                      <h2 className="contact-title">Get In Touch</h2>
                      <p className="contact-subtitle">We are here for you! How can we help?</p>
                      
                      {/* Success message at the top of the form */}
                      {submitMessage && submitMessage.includes('Thank you') && (
                        <div className="alert alert-success mb-4">
                          {submitMessage}
                        </div>
                      )}
                      
                      <form onSubmit={handleSubmit} className="contact-form">
                        <div className="mb-4">
                          <input
                            type="text"
                            className="form-control contact-input"
                            placeholder="Enter your name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                          />
                          {errors.name && <p className="text-danger mt-1 mb-0 small">{errors.name}</p>}
                        </div>
                        
                        <div className="mb-4">
                          <input
                            type="text"
                            className="form-control contact-input"
                            placeholder="Enter your email address"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                          />
                          {errors.email && <p className="text-danger mt-1 mb-0 small">{errors.email}</p>}
                        </div>

                        <div className="mb-4">
                          <TextField
                            fullWidth
                            type="text"
                            placeholder="Enter your mobile number"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#EFE8F9',
                                borderRadius: '8px',
                                border: '1px solid transparent',
                                padding: '0px',
                                fontSize: '14px',
                                transition: 'all 0.3s ease',
                                '& fieldset': {
                                  border: 'none',
                                },
                                '&:hover': {
                                  backgroundColor: '#EFE8F9',
                                  border: '1px solid transparent',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #6318c6',
                                  boxShadow: '0 0 0 0.2rem rgba(99, 24, 198, 0.25)',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '18px 20px',
                                fontSize: '14px',
                                color: '#0E0E0E',
                                '&::placeholder': {
                                  color: '#999999',
                                  fontSize: '14px',
                                },
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ marginLeft: '12px', marginRight: '4px' }}>
                                  <Box
                                    onClick={handleCountryClick}
                                    sx={{
                                      backgroundColor: '#E0DBE6',
                                      px: '10px',
                                      py: '8px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: '#D4CFDB',
                                      }
                                    }}
                                  >
                                    <Typography sx={{ color: '#6218C5', fontSize: '14px', fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                                      {formData.mobile_code}
                                    </Typography>
                                    <ArrowDropDownIcon sx={{ fontSize: 16, color: '#6218C5' }} />
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />

                          <Popover
                            open={Boolean(countryAnchorEl)}
                            anchorEl={countryAnchorEl}
                            onClose={handleCountryClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            PaperProps={{
                              sx: {
                                width: '300px',
                                maxHeight: '350px',
                                borderRadius: '12px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                mt: 0.5,
                              }
                            }}
                          >
                            <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Search country..."
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                  }
                                }}
                              />
                            </Box>
                            <List sx={{ p: 0, overflow: 'auto', maxHeight: '280px' }}>
                              {filteredCountries.map((country) => (
                                <ListItemButton
                                  key={country.id}
                                  onClick={() => handleCountrySelect(country)}
                                  selected={country.country_code === formData.mobile_code}
                                  sx={{
                                    py: 0.8,
                                    px: 2,
                                    '&.Mui-selected': {
                                      backgroundColor: '#f3e8ff',
                                    },
                                    '&:hover': {
                                      backgroundColor: '#f9f5ff',
                                    },
                                  }}
                                >
                                  <ListItemText
                                    primary={`${country.country_code} ${country.country_name}`}
                                  />
                                </ListItemButton>
                              ))}
                              {filteredCountries.length === 0 && (
                                <ListItemButton disabled>
                                  <ListItemText primary="No countries found" />
                                </ListItemButton>
                              )}
                            </List>
                          </Popover>

                          {errors.mobile && <p className="text-danger mt-1 mb-0 small">{errors.mobile}</p>}
                        </div>

                        <div className="mb-4">
                          <textarea
                            className="form-control contact-textarea"
                            placeholder="Go ahead, We are listening..."
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows="4"
                          ></textarea>
                          {errors.message && <p className="text-danger mt-1 mb-0 small">{errors.message}</p>}
                        </div>

                        {recaptchaSiteKey && (
                          <div className="mb-4">
                            <div
                              ref={recaptchaWrapperRef}
                              className="contact-recaptcha"
                              style={{ height: RECAPTCHA_HEIGHT * recaptchaScale }}
                            >
                              <div
                                ref={recaptchaContainerRef}
                                className="contact-recaptcha-widget"
                                style={{ transform: `scale(${recaptchaScale})` }}
                              ></div>
                            </div>
                            {errors.recaptcha && <p className="text-danger mt-1 mb-0 small">{errors.recaptcha}</p>}
                          </div>
                        )}

                        <button 
                          type="submit" 
                          className="btn contact-submit-btn"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Sending...' : 'Submit'}
                        </button>
                        
                        {/* Error messages below submit button */}
                        {submitMessage && !submitMessage.includes('Thank you') && (
                          <div className="alert alert-danger mt-3">
                            {submitMessage}
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                  
                  {/* Right Section - Illustration and Contact Info */}
                  <div className="col-lg-6">
                    <div className="contact-info-section">
                      <div className="contact-illustration">
                        <img 
                          src="/assets/img/website/contact-right.svg" 
                          alt="Contact Illustration" 
                          className="img-fluid"
                        />
                      </div>
                      
                      <div className="contact-details">
                        <div className="contact-item">
                          <div className="contact-icon">
                            <img 
                              src="/assets/img/website/location.svg" 
                              alt="Location" 
                              className="icon-img"
                            />
                          </div>
                          <div className="contact-text">
                            <p className='mb-0'>Crystal Plaza, <span className='font-montserrat'>604/605</span>,<br /> Chakala, Andheri East,<br /> Mumbai, Maharashtra <span className='font-montserrat'>400099</span></p>
                          </div>
                        </div>
                        
                        <div className="contact-item">
                          <div className="contact-icon">
                            <img 
                              src="/assets/img/website/whatsapp.svg" 
                              alt="Phone" 
                              className="icon-img"
                            />
                          </div>
                          <div className="contact-text pt-2">
                            <a href="tel:+91 7039796526"><span className='font-montserrat'>+91 7039796526</span></a>
                          </div>
                        </div>
                        
                        <div className="contact-item">
                          <div className="contact-icon">
                            <img 
                              src="/assets/img/website/message.svg" 
                              alt="Email" 
                              className="icon-img"
                            />
                          </div>
                            <div className="contact-text pt-2">
                            <a href="mailto:sales@glammify.ai">sales<span className='font-montserrat'>@</span>glammify.ai</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      
      <Footer />
    </div>
  );
};

export default ContactUsPage;
